import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle, Check, Upload, X } from "lucide-react";
import AdminHeader from "../../../components/admin/common/AdminHeader";
import AdminBreadcrumb from "../../../components/admin/common/AdminBreadcrumb";
import { productService } from "../../../services/productService";
import type { CreateProductData } from "../../../types/product";
import { categoryService } from "../../../services/categoryService";

const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`;

interface Category {
  id: string;
  name: string;
}

interface Brand {
  id: string;
  name: string;
}

const CreateProduct = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<
    Array<{ url: string; alt: string }>
  >([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    brand: "",
    sku: "",
    stock: "",
    featured: false,
    specifications: {
      gsm: "",
      size: "",
      sheets: "",
    },
  });

  // Fetch categories and brands
  useEffect(() => {
    const fetchCategoriesAndBrands = async () => {
      try {
        setLoadingOptions(true);
        const [categoriesData, brandsData] = await Promise.all([
          categoryService.getAllCategories(),
          categoryService.getAllBrands(),
        ]);

        console.log("Fetched categories:", categoriesData);
        console.log("Fetched brands:", brandsData);

        setCategories(categoriesData);
        setBrands(brandsData);
      } catch (err) {
        console.error("Error fetching categories and brands:", err);
        setError("Failed to load categories and brands");
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchCategoriesAndBrands();
  }, []);

  // Define Cloudinary response type
  interface CloudinaryResponse {
    secure_url: string;
    public_id: string;
    asset_id: string;
    resource_type: string;
    url: string;
    [key: string]: any; // Allow other properties
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    if (
      !process.env.REACT_APP_CLOUDINARY_CLOUD_NAME ||
      !process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
    ) {
      console.error("Cloudinary configuration missing");
      setError(
        "Cloudinary configuration is missing. Please check your environment variables."
      );
      return;
    }

    try {
      for (const file of files) {
        console.log("Uploading file:", file.name, "Size:", file.size);

        const formData = new FormData();
        formData.append("file", file);
        formData.append(
          "upload_preset",
          process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
        );
        formData.append(
          "cloud_name",
          process.env.REACT_APP_CLOUDINARY_CLOUD_NAME
        );

        console.log("Starting upload to:", CLOUDINARY_UPLOAD_URL);
        console.log(
          "Upload preset:",
          process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
        );
        console.log("Cloud name:", process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);

        const response = await fetch(CLOUDINARY_UPLOAD_URL, {
          method: "POST",
          body: formData,
        });

        // Log the raw response for debugging
        const responseText = await response.text();
        console.log("Raw response:", responseText);

        if (!response.ok) {
          throw new Error(
            `Upload failed with status ${response.status}: ${responseText}`
          );
        }

        let data: CloudinaryResponse;
        try {
          data = JSON.parse(responseText) as CloudinaryResponse;
        } catch (e) {
          console.error("Failed to parse response:", e);
          throw new Error("Invalid response from server");
        }

        console.log("Upload successful:", data);

        if (!data.secure_url) {
          throw new Error("No URL in response");
        }

        setUploadedImages((prev) => [
          ...prev,
          {
            url: data.secure_url,
            alt: file.name,
          },
        ]);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to upload images. Please try again."
      );
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.category) {
        throw new Error("Please fill in all required fields");
      }

      // Validate images
      if (uploadedImages.length === 0) {
        throw new Error("Please upload at least one product image");
      }

      const productData: CreateProductData = {
        ...formData,
        price: formData.price ? Number(formData.price) : null,
        stock: Number(formData.stock),
        status: "active" as const,
        images: uploadedImages,
        slug: formData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)+/g, ""),
      };

      console.log("Submitting product data:", productData);

      const productId = await productService.createProduct(productData);
      console.log("Product created with ID:", productId);

      setSuccessMessage("Product created successfully!");

      // Navigate after a short delay to show success message
      setTimeout(() => {
        navigate("/admin/products");
      }, 1500);
    } catch (err) {
      console.error("Error creating product:", err);
      setError(err instanceof Error ? err.message : "Failed to create product");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <AdminHeader
        title="Create Product"
        onMenuClick={() => {}}
        actions={
          <button
            onClick={() => navigate("/admin/products")}
            className="inline-flex items-center px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
        }
      />

      <div className="container mx-auto px-4">
        <AdminBreadcrumb
          items={[
            { label: "Products", href: "/admin/products" },
            { label: "Create Product" },
          ]}
        />

        <div className="mt-6 max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6">
            {error && (
              <div className="mb-6 flex items-center p-4 text-red-600 bg-red-50 rounded-lg">
                <AlertCircle className="w-5 h-5 mr-2" />
                <p>{error}</p>
              </div>
            )}

            {successMessage && (
              <div className="mb-6 flex items-center p-4 text-green-600 bg-green-50 rounded-lg">
                <Check className="w-5 h-5 mr-2" />
                <p>{successMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Images *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                  <div className="space-y-1 text-center">
                    <div className="flex flex-col items-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-brand-blue-500 hover:text-brand-blue-600"
                        >
                          <span>Upload images</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            accept="image/*"
                            onChange={handleImageUpload}
                          />
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  </div>
                </div>

                {/* Preview uploaded images */}
                {uploadedImages.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image.url}
                          alt={image.alt}
                          className="h-24 w-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rest of your existing form fields... */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (KES)
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock *
                  </label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) =>
                      setFormData({ ...formData, stock: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                    required
                    disabled={loadingOptions}
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand *
                  </label>
                  <select
                    value={formData.brand}
                    onChange={(e) =>
                      setFormData({ ...formData, brand: e.target.value })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                    required
                    disabled={loadingOptions}
                  >
                    <option value="">Select Brand</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.name}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Specifications */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GSM
                  </label>
                  <input
                    type="text"
                    value={formData.specifications.gsm}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        specifications: {
                          ...formData.specifications,
                          gsm: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Size
                  </label>
                  <input
                    type="text"
                    value={formData.specifications.size}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        specifications: {
                          ...formData.specifications,
                          size: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sheets
                  </label>
                  <input
                    type="text"
                    value={formData.specifications.sheets}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        specifications: {
                          ...formData.specifications,
                          sheets: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Featured Product Settings
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={formData.featured}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            featured: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-brand-blue-500 focus:ring-brand-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="featured"
                        className="ml-2 text-sm text-gray-700"
                      >
                        Show in Featured Section
                      </label>
                    </div>

                    <div className="text-sm text-gray-500">
                      <p>
                        When enabled, this product will appear in the featured
                        section under its category:{" "}
                        <strong>
                          {formData.category || "No category selected"}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate("/admin/products")}
                  className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-white bg-brand-blue-500 rounded-lg hover:bg-brand-blue-600 
                    disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    "Create Product"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProduct;
